import axios from 'axios';

const API_BASE_URL = '/api/osc-reports';

class OSCReportService {
  /**
   * Gera e baixa PDF do relatório de OSC
   * @param {Object} filters - Filtros para o relatório
   * @param {string} filters.selectedOSC - OSC selecionada
   * @param {string} filters.selectedYear - Ano selecionado
   * @param {string} filters.template - Template a ser usado (opcional)
   * @param {AbortSignal} signal - Signal para cancelamento da requisição
   * @returns {Promise<void>}
   */
  static async generatePDF(filters = {}, signal = null) {
    try {
      
      const response = await axios.post(`${API_BASE_URL}/pdf`, filters, {
        responseType: 'arraybuffer', // Usar arraybuffer para dados binários
        timeout: 180000, // 3 minutos timeout para PDFs grandes
        signal: signal, // Adicionar signal para cancelamento
        withCredentials: true, // ✅ Enviar cookies automaticamente
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Verificar se a resposta é válida
      if (!response.data || response.data.byteLength === 0) {
        throw new Error('PDF vazio ou inválido recebido do servidor');
      }

      // Criar blob do PDF a partir do ArrayBuffer
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      
      // Extrair nome do arquivo do header Content-Disposition
      let fileName = 'relatorio_osc.pdf';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // Criar URL temporária e fazer download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL temporária
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        fileName,
        message: 'PDF gerado e baixado com sucesso!'
      };

    } catch (error) {
      // Se foi cancelado, re-throw o erro original para manter o tipo
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        throw error;
      }
      
      let errorMessage = 'Erro ao gerar PDF do relatório';
      
      if (error.response) {
        // Erro da API
        if (error.response.status === 404) {
          errorMessage = 'Nenhuma parceria encontrada com os filtros aplicados';
        } else if (error.response.status === 500) {
          errorMessage = 'Erro interno do servidor ao gerar PDF';
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.request) {
        // Erro de rede
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.code === 'ECONNABORTED') {
        // Timeout
        errorMessage = 'Timeout: O relatório está demorando muito para ser gerado. Tente com filtros mais específicos.';
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Formata filtros para envio à API
   * @param {string} selectedOSC 
   * @param {string} selectedYear 
   * @param {string} template - Template a ser usado (opcional)
   * @returns {Object}
   */
  static formatFilters(selectedOSC, selectedYear, template = 'institutional') {
    return {
      selectedOSC: selectedOSC || 'Todas as OSCs',
      selectedYear: selectedYear || 'Todos os Anos',
      template: template
    };
  }

  /**
   * Gera declaração OSC em PDF usando a rota específica
   * @param {string} cnpj - CNPJ da OSC
   * @param {string} selectedYear - Ano selecionado para filtrar parcerias (opcional)
   * @returns {Promise<Object>}
   */
  static async generateDeclarationPDF(cnpj, selectedYear = 'Todos os Anos') {
    try {
      // CRÍTICO: Codificar CNPJ para URL (barras e pontos quebram a rota)
      const encodedCnpj = encodeURIComponent(cnpj);
      
      // Usar a rota específica de declaração que já prepara os dados
      const response = await axios.post(`${API_BASE_URL}/declaration/${encodedCnpj}`, {
        selectedYear // Enviar ano selecionado no body
      }, {
        responseType: 'arraybuffer',
        timeout: 120000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // A OSC sem pendências responde 200 com um JSON (não um PDF) — é uma recusa de
      // regra de negócio esperada, não um erro de rede. Como responseType é 'arraybuffer',
      // o corpo chega como bytes independente do tipo; distinguir pelo Content-Type real.
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const decoder = new TextDecoder('utf-8');
        const data = JSON.parse(decoder.decode(response.data));
        const businessError = new Error(data.message || 'Declaração não disponível para esta OSC');
        businessError.isBusinessRuleRefusal = true;
        throw businessError;
      }

      // Verificar se a resposta é válida
      if (!response.data || response.data.byteLength === 0) {
        throw new Error('PDF de declaração vazio ou inválido recebido do servidor');
      }

      // Criar blob do PDF
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      
      // Extrair nome do arquivo do header
      const cleanedCnpj = cnpj.replace(/[^\d]/g, '');
      let fileName = `declaracao_${cleanedCnpj}_${new Date().toISOString().split('T')[0]}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // Criar URL temporária para download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      // Simular clique para download
      document.body.appendChild(link);
      link.click();
      
      // Limpeza
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        fileName,
        message: 'Declaração gerada e baixada com sucesso'
      };

    } catch (error) {
      // Recusa de regra de negócio (ex.: sem pendências) já vem com a mensagem certa —
      // não é um erro de rede/servidor, então não passa pelas heurísticas abaixo.
      if (error.isBusinessRuleRefusal) {
        throw error;
      }

      let errorMessage = 'Erro ao gerar declaração PDF';

      if (error.response && error.response.status === 404) {
        // CRÍTICO: Quando responseType é 'arraybuffer', erros JSON vêm como ArrayBuffer
        // Precisamos converter para string e depois para JSON
        try {
          let errorData = error.response.data;
          
          // Se for ArrayBuffer, converter para string
          if (errorData instanceof ArrayBuffer) {
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(errorData);
            errorData = JSON.parse(text);
          }
          
          // Extrair mensagem do erro
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = 'OSC não encontrada ou sem parcerias registradas';
          }
        } catch (parseError) {
          errorMessage = 'OSC não encontrada ou sem parcerias registradas';
        }
      } else if (error.response && error.response.data) {
        // Tentar extrair mensagem de outros erros
        try {
          let errorData = error.response.data;
          if (errorData instanceof ArrayBuffer) {
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(errorData);
            errorData = JSON.parse(text);
          }
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Silenciosamente ignora erro de parse
        }
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Timeout: A declaração está demorando muito para ser gerada.';
      }

      throw new Error(errorMessage);
    }
  }
}

export default OSCReportService;
